// frontend/src/components/Leads/TeamAssignmentModal.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService';
import LoadingSpinner from '../LoadingSpinner';

// Этот компонент будет использоваться для назначения адвоката и паралигала
// для конвертированного дела.
const TeamAssignmentModal = ({ caseId, caseName, onClose, onAssignmentSuccess }) => {
    // Состояние для хранения списка доступных сотрудников
    const [staff, setStaff] = useState({ attorneys: [], paralegals: [] });
    // Состояние для хранения выбранного адвоката и паралигала
    const [selectedAttorney, setSelectedAttorney] = useState('');
    const [selectedParalegal, setSelectedParalegal] = useState('');
    // Состояние для отслеживания загрузки данных
    const [isLoading, setIsLoading] = useState(true);
    // Состояние для отслеживания процесса назначения
    const [isAssigning, setIsAssigning] = useState(false);

    // Хук для загрузки списка доступных сотрудников (адвокатов и паралигалов)
    useEffect(() => {
        const fetchStaff = async () => {
            try {
                // Предполагаем, что есть эндпоинты для получения сотрудников по роли
                const attorneys = await apiService.getUsersByRole('ATTORNEY');
                const paralegals = await apiService.getUsersByRole('PARALEGAL');
                
                setStaff({ attorneys, paralegals });
                
                // Предварительно выбираем первого доступного сотрудника, если есть
                if (attorneys.length > 0) {
                    setSelectedAttorney(attorneys[0].id);
                }
                if (paralegals.length > 0) {
                    setSelectedParalegal(paralegals[0].id);
                }

            } catch (error) {
                toast.error(`Failed to load staff: ${error.message}`);
                onClose(); // Закрываем модальное окно при ошибке загрузки
            } finally {
                setIsLoading(false);
            }
        };
        fetchStaff();
    }, [onClose]);

    // Обработчик для отправки данных о назначении
    const handleSubmit = async () => {
        // Проверка, что выбраны оба сотрудника (или хотя бы один, если это не обязательно)
        if (!selectedAttorney && staff.attorneys.length > 0) {
            toast.error('Please select an Attorney.');
            return;
        }
        
        if (!selectedParalegal && staff.paralegals.length > 0) {
            toast.error('Please select a Paralegal.');
            return;
        }
        
        // Формируем данные для API
        const assignmentData = {
            attorney_id: selectedAttorney || null,
            paralegal_id: selectedParalegal || null,
        };

        try {
            setIsAssigning(true);
            // Вызываем новый метод API для назначения команды
            await apiService.assignTeamToCase(caseId, assignmentData);
            
            toast.success(`Team assigned to case ${caseName} successfully!`);
            onAssignmentSuccess(); // Вызываем колбэк для обновления состояния на LeadsPage
            onClose(); // Закрываем модальное окно

        } catch (error) {
            toast.error(`Assignment failed: ${error.message}`);
        } finally {
            setIsAssigning(false);
        }
    };

    if (isLoading) {
        return (
            <div className="modal-backdrop">
                <div className="modal-content">
                    <LoadingSpinner message="Loading staff..." />
                </div>
            </div>
        );
    }

    // Проверяем, есть ли вообще сотрудники для назначения
    const hasStaff = staff.attorneys.length > 0 || staff.paralegals.length > 0;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Assign Team to Case: {caseName}</h2>
                    <button onClick={onClose} className="modal-close-btn">&times;</button>
                </div>
                <div className="modal-body">
                    {!hasStaff && (
                        <p className="text-danger">No Attorneys or Paralegals found. Please add staff members first.</p>
                    )}
                    {hasStaff && (
                        <form>
                            <div className="form-group">
                                <label htmlFor="attorney-select">Attorney (Адвокат)</label>
                                <select
                                    id="attorney-select"
                                    className="form-control"
                                    value={selectedAttorney}
                                    onChange={(e) => setSelectedAttorney(e.target.value)}
                                    disabled={staff.attorneys.length === 0 || isAssigning}
                                >
                                    {staff.attorneys.length === 0 ? (
                                        <option value="">No Attorneys available</option>
                                    ) : (
                                        <>
                                            <option value="">-- Select Attorney --</option>
                                            {staff.attorneys.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.full_name} ({user.email})
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                            
                            <div className="form-group mt-3">
                                <label htmlFor="paralegal-select">Paralegal (Паралигал)</label>
                                <select
                                    id="paralegal-select"
                                    className="form-control"
                                    value={selectedParalegal}
                                    onChange={(e) => setSelectedParalegal(e.target.value)}
                                    disabled={staff.paralegals.length === 0 || isAssigning}
                                >
                                    {staff.paralegals.length === 0 ? (
                                        <option value="">No Paralegals available</option>
                                    ) : (
                                        <>
                                            <option value="">-- Select Paralegal --</option>
                                            {staff.paralegals.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.full_name} ({user.email})
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                        </form>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        onClick={onClose} 
                        className="btn btn-secondary"
                        disabled={isAssigning}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSubmit} 
                        className="btn btn-primary"
                        disabled={isAssigning || !hasStaff}
                    >
                        {isAssigning ? 'Assigning...' : 'Assign Team'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TeamAssignmentModal;